import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { mfaClient } from '@/api/mfa/client';
import { getMfaCredentialsQuery, invalidateMfaCredentials } from '@/api/mfa/queries';
import { type MfaCredential, MfaMethod, type MfaPasskeyEnrollment } from '@/api/mfa/types';
import {
  forgetLastUsedPasskeyCredential,
  forgetPasskeyLogin,
  rememberLastUsedPasskeyCredential,
  rememberPasskeyLogin,
} from '@/features/auth/passkey-login-preference';
import { getMfaErrorMessage } from '@/features/mfa/mfa-errors';
import {
  MfaVerificationCancelledError,
  type MfaVerificationController,
} from '@/features/mfa/use-mfa-verification';
import { startOwnedRegistration } from '@/features/mfa/webauthn-ceremony';

export type PasskeyCredential = {
  credentialId: string;
  friendlyCredentialName?: string;
  createdAt?: Date;
};

export type PasskeysManagementState =
  | { status: 'idle'; passkeys: PasskeyCredential[] }
  | { status: 'loading'; passkeys: PasskeyCredential[] }
  | { status: 'error'; passkeys: PasskeyCredential[]; message: string };

interface UsePasskeysManagementOptions {
  enabled?: boolean;
  verification: MfaVerificationController;
}

export type PasskeyAddOutcome = 'added' | 'already-on-device' | 'cancelled' | 'failed';

const CANCELLATION_ERROR_NAMES = new Set(['AbortError', 'NotAllowedError']);

const isCeremonyCancelled = (error: unknown): boolean =>
  error instanceof MfaVerificationCancelledError ||
  (typeof error === 'object' &&
    error !== null &&
    'name' in error &&
    CANCELLATION_ERROR_NAMES.has(String((error as { name: unknown }).name)));

// The browser refuses registration when an excluded (already enrolled) credential lives on this authenticator.
const isAlreadyOnDevice = (error: unknown): boolean =>
  typeof error === 'object' &&
  error !== null &&
  'name' in error &&
  error.name === 'InvalidStateError';

const toPasskey = (credential: MfaCredential): PasskeyCredential[] => {
  if (credential.method !== MfaMethod.Passkey) {
    return [];
  }
  const createdAt = new Date(credential.createdAt);
  return [
    {
      credentialId: credential.id,
      ...(credential.name ? { friendlyCredentialName: credential.name } : {}),
      ...(!Number.isNaN(createdAt.getTime()) ? { createdAt } : {}),
    },
  ];
};

const managementState = (
  passkeys: PasskeyCredential[],
  operationError: string | null,
  loading: boolean,
  failed: boolean,
): PasskeysManagementState => {
  if (operationError) {
    return { status: 'error', passkeys, message: operationError };
  }
  if (failed) {
    return { status: 'error', passkeys, message: 'Could not load passkeys. Try again.' };
  }
  if (loading) {
    return { status: 'loading', passkeys };
  }
  return { status: 'idle', passkeys };
};

export const usePasskeysManagement = ({
  enabled = true,
  verification,
}: UsePasskeysManagementOptions) => {
  const queryClient = useQueryClient();
  const credentialsQuery = useQuery({ ...getMfaCredentialsQuery(), enabled });
  const [operationError, setOperationError] = useState<string | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [passkeyToRemove, setPasskeyToRemove] = useState<PasskeyCredential | null>(null);
  const operationRef = useRef<Promise<unknown> | null>(null);
  const flowAttemptRef = useRef(0);
  const verificationAttemptRef = useRef<number | null>(null);
  const ceremonyRef = useRef<{ attempt: number; cancel: () => void } | null>(null);
  const mountedRef = useRef(true);

  const passkeys = useMemo(
    () => (credentialsQuery.data?.credentials ?? []).flatMap(toPasskey),
    [credentialsQuery.data],
  );

  useEffect(() => {
    if (!enabled || !credentialsQuery.isSuccess) {
      return;
    }
    if (passkeys.length > 0) {
      rememberPasskeyLogin();
    } else {
      forgetPasskeyLogin();
    }
  }, [credentialsQuery.isSuccess, enabled, passkeys.length]);

  const runExclusive = useCallback(<T>(action: () => Promise<T>): Promise<T> => {
    if (operationRef.current) {
      return operationRef.current as Promise<T>;
    }
    const request = action().finally(() => {
      if (operationRef.current === request) {
        operationRef.current = null;
      }
    });
    operationRef.current = request;
    return request;
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      flowAttemptRef.current += 1;
      if (ceremonyRef.current !== null) {
        ceremonyRef.current.cancel();
        ceremonyRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (enabled) {
      return;
    }
    setOperationError(null);
    setIsAdding(false);
    setDeletingId(null);
    setPasskeyToRemove(null);
  }, [enabled]);

  const onRetry = useCallback(async () => {
    setOperationError(null);
    await credentialsQuery.refetch();
  }, [credentialsQuery]);

  const isCurrentFlow = useCallback(
    (attempt: number) => mountedRef.current && flowAttemptRef.current === attempt,
    [],
  );

  const startEnrollment = useCallback(
    async (attempt: number): Promise<MfaPasskeyEnrollment | null> => {
      verificationAttemptRef.current = attempt;
      const enrollment = await verification.runProtected((proof) =>
        mfaClient.startEnrollment({ method: MfaMethod.Passkey }, proof),
      );
      if (verificationAttemptRef.current === attempt) {
        verificationAttemptRef.current = null;
      }
      if (enrollment.method !== MfaMethod.Passkey) {
        throw new Error('Unexpected MFA enrollment method.');
      }
      return isCurrentFlow(attempt) ? enrollment : null;
    },
    [isCurrentFlow, verification],
  );

  const confirmEnrollment = useCallback(
    async (attempt: number, enrollment: MfaPasskeyEnrollment): Promise<boolean> => {
      const expiresAt = Date.parse(enrollment.expiresAt);
      const expired = () =>
        Object.assign(new Error('MFA enrollment expired.'), {
          data: { code: 'USER.MFA.WORKFLOW_INVALID' },
        });
      if (!Number.isFinite(expiresAt) || expiresAt <= Date.now()) throw expired();
      const ceremony = startOwnedRegistration({ optionsJSON: enrollment.publicKey });
      let rejectWait!: (_error: unknown) => void;
      const timeout = new Promise<never>((_resolve, reject) => {
        rejectWait = reject;
      });
      const expiry = setTimeout(
        () => {
          ceremony.cancel();
          rejectWait(expired());
        },
        Math.min(expiresAt - Date.now(), 2_147_483_647),
      );
      ceremonyRef.current = {
        attempt,
        cancel: () => {
          clearTimeout(expiry);
          ceremony.cancel();
          rejectWait(new MfaVerificationCancelledError());
        },
      };
      try {
        const credential = await Promise.race([ceremony.result, timeout]);
        clearTimeout(expiry);
        if (!isCurrentFlow(attempt) || ceremonyRef.current?.attempt !== attempt) return false;
        ceremonyRef.current = null;
        if (expiresAt <= Date.now()) throw expired();
        await mfaClient.confirmPasskeyEnrollment(enrollment.interactionToken, credential);
        if (isCurrentFlow(attempt)) {
          rememberLastUsedPasskeyCredential({
            id: credential.id,
            userHandle: enrollment.publicKey.user.id,
          });
          rememberPasskeyLogin();
        }
        await invalidateMfaCredentials(queryClient);
        return true;
      } finally {
        clearTimeout(expiry);
        if (ceremonyRef.current?.attempt === attempt) ceremonyRef.current = null;
      }
    },
    [isCurrentFlow, queryClient],
  );

  const handleAddError = useCallback(
    (attempt: number, error: unknown): PasskeyAddOutcome => {
      if (verificationAttemptRef.current === attempt) {
        verificationAttemptRef.current = null;
      }
      if (ceremonyRef.current?.attempt === attempt) {
        ceremonyRef.current = null;
      }
      if (!isCurrentFlow(attempt) || isCeremonyCancelled(error)) {
        return 'cancelled';
      }
      if (isAlreadyOnDevice(error)) {
        setOperationError('This device already has a passkey for your account.');
        return 'already-on-device';
      }
      setOperationError(getMfaErrorMessage(error, 'Could not add passkey. Try again.'));
      return 'failed';
    },
    [isCurrentFlow],
  );

  const addPasskey = useCallback(async (): Promise<PasskeyAddOutcome> => {
    const attempt = flowAttemptRef.current + 1;
    flowAttemptRef.current = attempt;
    setOperationError(null);
    setIsAdding(true);
    try {
      const enrollment = await startEnrollment(attempt);
      const confirmed = enrollment ? await confirmEnrollment(attempt, enrollment) : false;
      return confirmed ? 'added' : 'cancelled';
    } catch (error) {
      return handleAddError(attempt, error);
    } finally {
      if (isCurrentFlow(attempt)) {
        setIsAdding(false);
      }
    }
  }, [confirmEnrollment, handleAddError, isCurrentFlow, startEnrollment]);

  const add = useCallback((): Promise<PasskeyAddOutcome> => {
    if (!enabled) {
      return Promise.resolve('cancelled');
    }
    return runExclusive(addPasskey);
  }, [addPasskey, enabled, runExclusive]);

  const onAdd = useCallback(async () => {
    await add();
  }, [add]);

  const cancelAdd = useCallback(() => {
    if (!isAdding) {
      return;
    }
    const cancelledAttempt = flowAttemptRef.current;
    flowAttemptRef.current += 1;
    if (ceremonyRef.current !== null) {
      ceremonyRef.current.cancel();
      ceremonyRef.current = null;
    }
    if (verificationAttemptRef.current === cancelledAttempt) {
      verificationAttemptRef.current = null;
      verification.cancel();
    }
    operationRef.current = null;
    setIsAdding(false);
  }, [isAdding, verification]);

  const removePasskey = useCallback(
    async (credentialId: string): Promise<void> => {
      setOperationError(null);
      setDeletingId(credentialId);
      try {
        await verification.runProtected((proof) => mfaClient.removeCredential(credentialId, proof));
        forgetLastUsedPasskeyCredential();
        if (!mountedRef.current) {
          return;
        }
        setPasskeyToRemove(null);
        await invalidateMfaCredentials(queryClient);
      } catch (error) {
        if (mountedRef.current && !isCeremonyCancelled(error)) {
          setOperationError(getMfaErrorMessage(error, 'Could not remove passkey. Try again.'));
        }
      } finally {
        if (mountedRef.current) {
          setDeletingId(null);
        }
      }
    },
    [queryClient, verification],
  );

  const onConfirmDelete = useCallback(() => {
    if (!enabled || !passkeyToRemove) {
      return Promise.resolve();
    }
    return runExclusive(() => removePasskey(passkeyToRemove.credentialId));
  }, [enabled, passkeyToRemove, removePasskey, runExclusive]);

  const state = managementState(
    passkeys,
    operationError,
    credentialsQuery.isLoading,
    credentialsQuery.isError,
  );

  return {
    state,
    isAdding,
    deletingId,
    passkeyToRemove,
    add,
    onAdd,
    cancelAdd,
    onRetry,
    onDelete: setPasskeyToRemove,
    onCancelDelete: () => setPasskeyToRemove(null),
    onConfirmDelete,
  };
};
